/**
 * Script de personalización adaptado para páginas de colección
 * Maneja múltiples instancias de canvas simultáneamente
 * Incluye soporte para scroll infinito y carga dinámica de fuentes
 */

// Prevenir carga múltiple
if (typeof window.COLLECTION_CUSTOMIZATION_EVENTS !== 'undefined') {
} else {

const COLLECTION_CUSTOMIZATION_EVENTS = {
  PLAYER_CHANGE: "collection_customization_player_change",
  USER_CHANGE: "collection_customization_user_change"
};
const TARGET_COLLECTION_HANDLE = 'compra-por-jugador';
const TARGET_COLLECTION_TEMPLATE = 'collection.compra-por-jugador';

function normalizeFontHandle(rawValue) {
  const normalized = (rawValue || '').toString().trim().toLowerCase();
  if (!normalized || normalized === 'none') return 'laliga-font';

  if (
    normalized === 'champions-villa' ||
    normalized === 'champions villa' ||
    normalized.includes('champions')
  ) {
    return 'champions-villa';
  }

  if (
    normalized === 'laliga-font' ||
    normalized === 'la liga font' ||
    normalized === 'laliga font' ||
    normalized === 'la lliga font' ||
    normalized === 'lliga font' ||
    normalized.includes('laliga') ||
    normalized.includes('la liga') ||
    normalized.includes('lliga') ||
    normalized.includes('liga')
  ) {
    return 'laliga-font';
  }

  if (normalized.includes('dagger')) {
    return 'DaggerSquare';
  }

  return rawValue;
}

function resolveCanvasFontFamily(fontSetting) {
  const normalizedHandle = normalizeFontHandle(fontSetting);
  const rootStyles = getComputedStyle(document.documentElement);
  const cssVar = rootStyles.getPropertyValue(`--font-${normalizedHandle}`).trim();

  if (cssVar) {
    return cssVar.split(',')[0].replace(/['"]/g, '').trim();
  }

  // Fallback robusto cuando la variable CSS aún no está disponible
  if (normalizedHandle === 'laliga-font') return 'LaLiga Font';
  if (normalizedHandle === 'champions-villa') return 'Champions Villa';
  if (normalizedHandle === 'DaggerSquare') return 'DaggerSquare';

  return normalizedHandle || 'laliga-font';
}

function getCurrentCollectionHandle() {
  const pathMatch = window.location.pathname.match(/\/collections\/([^\/\?]+)/);
  return (pathMatch?.[1] || '').toLowerCase();
}

function isTargetPlayerCollection() {
  const templateName = document.querySelector('main[data-template]')?.dataset?.template || '';
  if (templateName === TARGET_COLLECTION_TEMPLATE) {
    return true;
  }

  // Fallback para entornos donde no esté disponible data-template
  return getCurrentCollectionHandle() === TARGET_COLLECTION_HANDLE;
}

class CollectionCustomizationManager {
  constructor() {
    this.canvasInstances = new Map();
    this.currentPlayer = null;
    this.currentName = null;
    this.currentNumber = null;
    this.searchParamsHandler = new SearchParamsHandler();
    this.isInitialized = false;
    this.mutationObserver = null;
    this.fontLoaded = false;
    this.playerDetector = null;
    this.imageElements = new Map(); // productId -> { slideshow, slides, cardGallery }
    this.mainCollectionRoot = null;
    
    this.init();
  }
  
  init() {
    if (this.isInitialized) return;
    if (!isTargetPlayerCollection()) {
      return;
    }

    // Limitar comportamiento al grid principal de colección.
    this.mainCollectionRoot = document.querySelector('.main-collection-grid');
    if (!this.mainCollectionRoot) {
      return;
    }
    
    // Cargar fuente DaggerSquare
    this.loadDaggerSquareFont();
    
    // Buscar todos los canvas de personalización en la página
    this.discoverCanvasInstances();
    
    // Buscar elementos de imagen para control de hover
    this.discoverImageElements();

    // Bloquear navegación manual del slider solo en productos principales
    this.lockMainGridSliders();
    
    // Configurar event listeners (incluye detector de jugador)
    this.setupEventListeners();
    
    // Configurar observer para scroll infinito
    this.setupInfiniteScrollObserver();
    
    // Cargar parámetros desde URL (después de configurar listeners)
    this.loadFromURL();

    // Propagar parámetros de personalización a los links de product cards
    this.propagateCustomizationParamsToProductLinks();

    // Re-render cuando las fuentes estén listas (evita fallback visual por timing)
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (this.currentPlayer) this.renderAllCanvas();
      });
    }
    
    // Configurar detector de jugador automático
    this.setupPlayerDetector();
    
    this.isInitialized = true;
  }
  
  discoverCanvasInstances() {
    const canvasElements = this.mainCollectionRoot
      ? this.mainCollectionRoot.querySelectorAll('.customization_canvas.collection-canvas')
      : [];

    canvasElements.forEach((canvas, index) => {
      const productId = canvas.dataset.productId;

      // Buscar el span con los datos específicos del producto
      const dataSpan = document.querySelector(`.product-customization-data[data-product-id="${productId}"]`);

      // Helper para leer data attribute con fallback
      const getData = (attr, fallback) => dataSpan?.dataset[attr] || canvas.dataset[attr] || fallback;

      // Obtener colores separados para nombre y dorsal
      const nameColor = getData('nameColor', '#FFFFFF');
      const numberColor = getData('numberColor', '#FFFFFF');

      // Obtener posición de imagen de preview
      const previewImageIndex = parseInt(getData('previewImageIndex', '1'), 10);

      // Leer configuraciones per-product desde data attributes (misma lógica que PDP)
      const settings = {
        nameSize: parseFloat(getData('nameSize', '37')),
        nameHeight: parseFloat(getData('nameHeight', '0.15')),
        numberSize: parseFloat(getData('numberSize', '160')),
        numberHeight: parseFloat(getData('numberHeight', '0.45')),
        horizontalAlignment: getData('horizontalAlignment', 'auto'),
        horizontalPosition: parseFloat(getData('horizontalPosition', '0.5')),
        characterLimitThreshold: parseInt(getData('characterLimitThreshold', '7'), 10),
        largeFontSize: parseFloat(getData('largeFontSize', '24')),
        smallFontSize: parseFloat(getData('smallFontSize', '18')),
        fontFamily: normalizeFontHandle(getData('fontFamily', 'laliga-font')),
      };

      if (productId && !this.canvasInstances.has(productId)) {
        this.canvasInstances.set(productId, {
          canvas: canvas,
          context: canvas.getContext('2d'),
          nameColor: nameColor,
          numberColor: numberColor,
          previewImageIndex: previewImageIndex,
          settings: settings,
          productId: productId,
          index: index
        });
      }
    });

  }

  discoverImageElements() {
    const cardGalleries = this.mainCollectionRoot
      ? this.mainCollectionRoot.querySelectorAll('.card-gallery')
      : [];
    
    cardGalleries.forEach((gallery, index) => {
      const productId = gallery.dataset.productId;
      
      if (!productId) {
        return;
      }
      
      const slideshow = gallery.querySelector('slideshow-container, [ref="slideshow"]');
      const slides = gallery.querySelectorAll('slideshow-slide, [data-slide]');
      
      if (slideshow && slides.length >= 2) {
        this.imageElements.set(productId, {
          gallery: gallery,
          slideshow: slideshow,
          slides: Array.from(slides),
          productId: productId,
          index: index
        });
      } else {
      }
    });
    
  }

  lockMainGridSliders() {
    if (!this.mainCollectionRoot) return;

    const cardGalleries = this.mainCollectionRoot.querySelectorAll('.card-gallery');
    cardGalleries.forEach((gallery) => {
      // Solo bloquear en productos principales con canvas de personalización de compra-por-jugador
      const hasPlayerCustomizationCanvas = !!gallery.querySelector('.customization_canvas.collection-canvas');
      if (!hasPlayerCustomizationCanvas) return;

      if (gallery.dataset.playerSliderLocked === 'true') return;

      const slideshowContainer = gallery.querySelector('slideshow-container');
      if (slideshowContainer) {
        // Mantener visible la media/canvas, pero bloquear swipe/drag/click en controles.
        slideshowContainer.style.pointerEvents = 'none';
      }

      const controls = gallery.querySelectorAll('slideshow-arrows, slideshow-controls');
      controls.forEach((control) => {
        control.style.display = 'none';
      });

      gallery.dataset.playerSliderLocked = 'true';
    });
  }
  
  // Método para cargar la fuente DaggerSquare
  loadDaggerSquareFont() {
    if (this.fontLoaded) return;
    
    // Usar la misma URL que en el script original
    const fontUrl = window.productCustomizerSettings?.fontUrl;
    if (!fontUrl) {
      return;
    }
    const fontFace = new FontFace('DaggerSquare', `url('${fontUrl}')`);
    
    fontFace.load().then((loadedFont) => {
      document.fonts.add(loadedFont);
      this.fontLoaded = true;
      
      // Re-renderizar todos los canvas después de cargar la fuente
      if (this.currentName || this.currentNumber) {
        this.renderAllCanvas();
      }
    }).catch((error) => {
      this.fontLoaded = false;
    });
  }
  
  // Observer para detectar nuevos productos cargados por scroll infinito
  setupInfiniteScrollObserver() {
    // Observar cambios en el grid de productos
    const productGrid = this.mainCollectionRoot
      ? this.mainCollectionRoot.querySelector('.product-grid, .collection-grid, [data-grid]')
      : null;
    
    if (!productGrid) {
      return;
    }
    
    this.mutationObserver = new MutationObserver((mutations) => {
      let newCanvasFound = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Buscar canvas en el nodo añadido
              const newCanvas = node.querySelectorAll ? 
                node.querySelectorAll('.customization_canvas.collection-canvas') : [];
              
              if (newCanvas.length > 0) {
                newCanvasFound = true;
              }
            }
          });
        }
      });
      
      if (newCanvasFound) {
        // Redescubrir canvas instances y elementos de imagen
        this.discoverCanvasInstances();
        this.discoverImageElements();
        this.lockMainGridSliders();
        this.propagateCustomizationParamsToProductLinks();
        
        // Aplicar personalización actual si existe
        if (this.currentName || this.currentNumber) {
          setTimeout(() => {
            this.renderAllCanvas();
            // Re-configurar comportamiento de imágenes si es colección de jugador
            if (this.currentPlayer) {
              this.setupPlayerCollectionImages();
            }
          }, 100); // Pequeño delay para asegurar que el DOM esté listo
        }
      }
    });
    
    this.mutationObserver.observe(productGrid, {
      childList: true,
      subtree: true
    });
    
  }

  propagateCustomizationParamsToProductLinks() {
    const currentUrl = new URL(window.location.href);
    const personalizationParams = new URLSearchParams();
    const playerParam = currentUrl.searchParams.get('player');

    // Si hay jugador en colección, en PDP siempre debemos entrar por flujo de jugador.
    if (playerParam) {
      personalizationParams.set('player', playerParam);
      personalizationParams.set('type', 'player');

      const genderParam = currentUrl.searchParams.get('gender');
      if (genderParam) {
        personalizationParams.set('gender', genderParam);
      }
    } else {
      const allowedParams = ['name', 'number', 'type', 'gender'];
      allowedParams.forEach((key) => {
        const value = currentUrl.searchParams.get(key);
        if (value) {
          personalizationParams.set(key, value);
        }
      });
    }

    if ([...personalizationParams.keys()].length === 0) {
      return;
    }

    const productLinks = (this.mainCollectionRoot || document).querySelectorAll(
      '.product-grid a[href*="/products/"], .card-gallery a[href*="/products/"], a.product-card__link[href]'
    );

    productLinks.forEach((link) => {
      try {
        const targetUrl = new URL(link.getAttribute('href'), window.location.origin);

        // Mantener variante/product URL original y agregar solo parámetros de personalización
        personalizationParams.forEach((value, key) => {
          targetUrl.searchParams.set(key, value);
        });

        link.setAttribute('href', `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`);
      } catch (error) {
        console.warn('[CollectionCustomizationManager] Could not append customization params to link:', link, error);
      }
    });
  }
  
  loadFromURL() {
    const urlPlayer = this.searchParamsHandler.getPlayer();
    const urlName = this.searchParamsHandler.getCustomizationName();
    const urlNumber = this.searchParamsHandler.getCustomizationNumber();
    
    if (urlPlayer) {
      this.setCustomization(urlPlayer, urlName, urlNumber);
    }
  }
  
  setCustomization(player, name = null, number = null) {
    this.currentPlayer = player;
    this.currentName = name;
    this.currentNumber = number;
    
    
    // Renderizar en todos los canvas
    this.renderAllCanvas();
    
    // Mostrar canvas con personalización
    this.showCustomizedCanvas();
    
    // Actualizar URL
    this.updateURL();
  }
  
  renderAllCanvas() {
    if (!this.currentPlayer) return;
    
    this.canvasInstances.forEach((instance, productId) => {
      this.renderCanvas(instance, this.currentName, this.currentNumber);
    });
  }
  
  renderCanvas(instance, name, number) {
    const { canvas, context, nameColor, numberColor } = instance;

    // Igual que PDP: el canvas interno debe seguir el tamaño real mostrado
    const rect = canvas.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      const nextWidth = Math.round(rect.width);
      const nextHeight = Math.round(rect.height);
      if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
        canvas.width = nextWidth;
        canvas.height = nextHeight;
      }
    }

    // Limpiar canvas
    context.clearRect(0, 0, canvas.width, canvas.height);

    if (!name && !number) return;

    // SIEMPRE convertir nombre a mayúsculas para renderizado en canvas de colección
    const collectionFontHandle = normalizeFontHandle((instance.settings || window.productCustomizerSettings || {}).fontFamily || 'laliga-font'); const displayName = name ? (String(collectionFontHandle || '').toLowerCase() === 'grogueta' ? name.toString().toLowerCase() : name.toString().toUpperCase()) : name;

    // Configurar propiedades de texto
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    // Usar configuraciones per-product desde data attributes, con fallback al global
    const settings = instance.settings || window.productCustomizerSettings || {};
    const nameHeightPercent = settings.nameHeight || 0.46;
    const numberHeightPercent = settings.numberHeight || 0.55;
    const alignmentMode = settings.alignmentMode || settings.horizontalAlignment || 'auto';
    const fontHandle = normalizeFontHandle(settings.fontFamily || 'laliga-font');
    const horizontalPosition = settings.horizontalPosition || 0.5;
    const REFERENCE_WIDTH = 1000;
    const scaleFactor = canvas.width / REFERENCE_WIDTH;

    // Resolver tipografía real de forma robusta ante valores traducidos del metafield
    const fontFamily = resolveCanvasFontFamily(fontHandle);

    // Calcular posición horizontal del texto (misma lógica que PDP)
    const textCenterX = alignmentMode === 'auto' ? canvas.width / 2 : canvas.width * horizontalPosition;

    if (displayName) {
      // Aplicar reglas y multiplicadores idénticos a PDP
      const threshold = settings.characterLimitThreshold || 7;
      const nameLength = displayName.length;
      let nameFontSize;

      if (nameLength < threshold && nameLength > 0) {
        nameFontSize = (settings.largeFontSize || 24) * 1.8;
      } else if (nameLength > 0) {
        nameFontSize = (settings.smallFontSize || 18) * 1.6;
      } else {
        nameFontSize = settings.nameSize || 67;
      }

      const scaledNameSize = Math.max(16, nameFontSize * scaleFactor);
      const finalNameFont = `${scaledNameSize}px '${fontFamily}', Arial, sans-serif`;
      context.font = finalNameFont;

      context.fillStyle = nameColor;
      const nameCenterY = canvas.height * nameHeightPercent;

      context.fillText(displayName, textCenterX, nameCenterY);
    }

    if (number) {
      // Misma escala que PDP: numberSize * scaleFactor
      const numberSize = settings.numberSize || 300;
      const scaledNumberSize = Math.max(16, numberSize * scaleFactor);

      const finalFont = `${scaledNumberSize}px '${fontFamily}', Arial, sans-serif`;
      context.font = finalFont;

      context.fillStyle = numberColor;
      const numberPositionY = canvas.height * numberHeightPercent;

      context.fillText(number, textCenterX, numberPositionY);
    }
  }
  
  calculateFontSize(text, maxWidth) {
    // Este método ya no se usa, pero se mantiene por compatibilidad
    const baseSize = 16;
    const maxSize = 24;
    const minSize = 12;
    
    // Calcular tamaño basado en longitud del texto
    let fontSize = Math.max(minSize, Math.min(maxSize, baseSize - (text.length - 5) * 1.5));
    
    return fontSize;
  }
  
  showCustomizedCanvas() {
    this.canvasInstances.forEach((instance, productId) => {
      if (this.currentPlayer) {
        instance.canvas.style.opacity = '1';
        instance.isVisible = true;
      } else {
        instance.canvas.style.opacity = '0';
        instance.isVisible = false;
      }
    });

    // Si es colección de jugador, configurar imágenes
    if (this.currentPlayer) {
      this.setupPlayerCollectionImages();
    } else {
      this.resetImageBehavior();
    }
  }

  setupPlayerCollectionImages() {
    this.imageElements.forEach((imageData, productId) => {
      const { slideshow, slides } = imageData;

      // Obtener la posición de imagen de preview desde la instancia del canvas (1-based position de Shopify)
      const canvasInstance = this.canvasInstances.get(productId);
      // El metafield almacena la posición de Shopify (1-based): 1 = primera imagen, 4 = cuarta imagen
      const previewImagePosition = canvasInstance?.previewImageIndex ?? 1;

      // Buscar el slide que corresponde a esta posición de medio
      const targetSlideIndex = slides.findIndex(slide => {
        const slidePosition = parseInt(slide.dataset.mediaPosition || '0', 10);
        return slidePosition === previewImagePosition;
      });

      if (targetSlideIndex !== -1) {
        // Encontramos el slide exacto por posición de medio
        this.showSlide(slideshow, targetSlideIndex);
      } else if (slides.length > 0) {
        // Fallback: si la posición no existe, usar el primer slide
        console.warn(`[CollectionCustomization] Position ${previewImagePosition} not found for product ${productId}. Using first slide.`);
        this.showSlide(slideshow, 0);
      } else {
        console.warn(`[CollectionCustomization] No slides available for product ${productId}.`);
      }
    });
  }


  showSlide(slideshow, index) {
    if (!slideshow) return;
    
    // Si es el componente slideshow del tema, usar su API
    if (typeof slideshow.select === 'function') {
      try {
        // slideshow.select(index, event, options)
        slideshow.select(index, null, { animate: false });
        return;
      } catch (e) {
        console.error('[CollectionCustomization] Error with slideshow.select:', e);
      }
    }

    // Método para mostrar un slide específico (Legacy/Fallback)
    if (slideshow.goToSlide) {
      slideshow.goToSlide(index);
    } else if (slideshow.slideTo) {
      slideshow.slideTo(index);
    } else {
      // Fallback: manipular directamente los slides
      const slides = slideshow.querySelectorAll('slideshow-slide, [data-slide]');
      slides.forEach((slide, i) => {
        if (i === index) {
          slide.style.display = 'block';
          slide.setAttribute('aria-hidden', 'false');
        } else {
          slide.style.display = 'none';
          slide.setAttribute('aria-hidden', 'true');
        }
      });
    }

  }

  resetImageBehavior() {
    this.imageElements.forEach((imageData) => {
      const { slideshow } = imageData;
      
      // Volver a la primera imagen por defecto
      this.showSlide(slideshow, 0);

    });
  }
  
  hideAllCanvas() {
    this.canvasInstances.forEach((instance) => {
      instance.canvas.style.opacity = '0';
      instance.isVisible = false;
    });
  }
  
  clearCustomization() {
    this.currentPlayer = null;
    this.currentName = null;
    this.currentNumber = null;
    
    // Limpiar todos los canvas
    this.canvasInstances.forEach((instance) => {
      instance.ctx.clearRect(0, 0, instance.canvas.width, instance.canvas.height);
    });
    
    // Ocultar canvas
    this.hideAllCanvas();
    
    // Limpiar URL
    this.searchParamsHandler.clearAll();
    
  }
  
  updateURL() {
    if (this.currentPlayer) {
      this.searchParamsHandler.setPlayer(this.currentPlayer);
    }
  }
  
  setupEventListeners() {
    // Escuchar cambios en la personalización desde otros componentes
    document.addEventListener(COLLECTION_CUSTOMIZATION_EVENTS.PLAYER_CHANGE, (event) => {
      const { player, name, number } = event.detail;
      this.setCustomization(player, name, number);
    });
    
    // Escuchar detección automática de jugador
    document.addEventListener('player-collection-detected', (event) => {
      const { playerData } = event.detail;
      
      if (playerData) {
        this.setCustomization(
          playerData.handle,
          playerData.fullName,
          playerData.number
        );
      }
    });
    
    // Escuchar cambios de parámetros URL
    window.addEventListener('popstate', () => {
      this.loadFromURL();
    });
  }
  
  setupPlayerDetector() {
    // Verificar si el detector de jugador está disponible
    if (window.playerCollectionDetector) {
      this.playerDetector = window.playerCollectionDetector;
      
      // Si ya se detectó un jugador, aplicar personalización
      const playerData = this.playerDetector.getPlayerData();
      if (playerData.isPlayerCollection && playerData.playerData) {
        this.setCustomization(
          playerData.playerData.handle,
          playerData.playerData.fullName,
          playerData.playerData.number
        );
      }
    } else {
    }
  }

  // Método público para establecer personalización desde otros scripts
  static setCustomization(player, name, number) {
    const event = new CustomEvent(COLLECTION_CUSTOMIZATION_EVENTS.PLAYER_CHANGE, {
      detail: { player, name, number }
    });
    document.dispatchEvent(event);
  }
}

// Clase SearchParamsHandler simplificada para colecciones
class SearchParamsHandler {
  getPlayer() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('player');
  }
  
  getCustomizationName() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('name');
  }
  
  getCustomizationNumber() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('number');
  }
  
  setPlayer(player) {
    const url = new URL(window.location);
    if (player) {
      url.searchParams.set('player', player);
    } else {
      url.searchParams.delete('player');
    }
    window.history.replaceState({}, '', url);
  }
  
  clearAll() {
    const url = new URL(window.location);
    url.searchParams.delete('player');
    url.searchParams.delete('name');
    url.searchParams.delete('number');
    window.history.replaceState({}, '', url);
  }
}

// Inicializar cuando el DOM esté listo
document.addEventListener('DOMContentLoaded', () => {
  // Solo inicializar en páginas de colección
  if (document.body.classList.contains('template-collection') || 
      window.location.pathname.includes('/collections/')) {
    if (isTargetPlayerCollection()) {
      window.collectionCustomizationManager = new CollectionCustomizationManager();
    }
  }
});

// Exportar para uso global
window.CollectionCustomizationManager = CollectionCustomizationManager;
window.COLLECTION_CUSTOMIZATION_EVENTS = COLLECTION_CUSTOMIZATION_EVENTS;

} // Fin del bloque de prevención de carga múltiple
